<script lang="ts" setup>
import { PropType, computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { NEW_ID } from '@/util/constants';
import { useEvents, Event } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { useStore } from 'vuex';
import { ContentStoreState, ContentStoreActions } from '@/stores/content.store';
import { DataResponse } from '@/services/base.service';
import { Discount, DiscountCode } from '@/models/discounts.model';
import { Initiative } from '@/models/initiative.model';
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import DateField from '@/components/common/DateField.vue';
import { addHours, format, parse } from 'date-fns';

const events = useEvents();
const store = useStore<ShiftCMSState & { cns: ContentStoreState }>();

const initiatives = computed<Initiative[]>(() => store.state.cns.initiatives);

const props = defineProps({
  data: {
    type: Object as PropType<Discount>,
    required: true,
  },
});

const model = ref<Discount>({
  ...props.data,
});

const codes = ref<number>(0);
const validity = ref<string>('');

const action = computed<string>(() =>
  model.value.id === NEW_ID ? 'Add' : 'Save',
);

interface CodeGroup {
  usedCodes: number;
  codes: number;
  validity: string | null;
}

const codeGroups = computed<CodeGroup[]>(() =>
  props.data.codes.reduce((acc: CodeGroup[], item: DiscountCode) => {
    let group = acc.find((d) => d.validity === item.validityDate) || {
      validity: item.validityDate,
      codes: 0,
      usedCodes: 0,
    };

    group.validity = item.validityDate;
    group.codes = props.data.codes.filter(
      (v) => v.validityDate === group.validity,
    ).length;
    group.usedCodes = props.data.codes.filter(
      (v) => v.used && v.validityDate === group.validity,
    ).length;

    return [...acc.filter((g) => g.validity !== item.validityDate), group];
  }, []),
);

const valid = ref<boolean>(true);

const cancel = () => {
  store.commit(Mutations.HideDrawer);
};

const saveItem = async () => {
  let res: DataResponse<Discount | null> = {
    status: false,
    data: null,
  };

  if (model.value.id === NEW_ID) {
    res = await store.dispatch(ContentStoreActions.CreateDiscount, model.value);
  } else {
    res = await store.dispatch(ContentStoreActions.UpdateDiscount, model.value);
  }

  if (res.status) {
    store.dispatch(Actions.ShowNotification, {
      message: `Saved Schedule`,
      type: 'success',
    });
    events.emit(Event.UpdateContentItem);
    cancel();
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: `Failed to saved Schedule`,
      type: 'error',
    });
  }
};

const removeItem = async () => {
  if (
    model.value &&
    model.value.id !== NEW_ID &&
    confirm(`Are you absolutely sure you want to delete this discount?`)
  ) {
    store.dispatch(ContentStoreActions.DeleteDiscount, model.value.id);

    store.commit(Mutations.HideDrawer);
  }
};

const updateLanguage = async () => {
  if (model.value.id === NEW_ID) {
    Object.assign(model, { title: '' });
  } else {
    const dis = await store.dispatch(
      ContentStoreActions.GetDiscount,
      model.value.id,
    );

    if (dis) {
      Object.assign(model.value, { ...dis });
    }
  }
};

onMounted(() => {
  events.on(Event.LanguageChanged, updateLanguage);
  if (props.data) {
    Object.assign(model.value, props.data);
  }
  store.dispatch(ContentStoreActions.GetInitiatives);
});

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateLanguage);
});

function parseCSV() {
  if (model.value.codesFile) {
    var reader = new FileReader();
    reader.readAsText(model.value.codesFile, 'UTF-8');

    reader.onload = function (evt: ProgressEvent<FileReader>) {
      const text = evt.target?.result;
      if (text && typeof text === 'string') {
        const lines = text.split('\n').filter((v) => !!v);
        lines.shift(); // remove header

        model.value.codes = [
          ...model.value.codes,
          ...lines.map((l) => {
            return {
              id: -1,
              code: l.split(',')[0],
              validityDate: validity.value
                ? addHours(
                    parse(validity.value, 'dd-MM-yyyy', new Date()),
                    12,
                  ).toISOString()
                : null,
              used: false,
            };
          }),
        ];
        codes.value = lines.length;
      }
    };
  }
}

function updateCodes() {
  // find new codes
  const codes = model.value.codes.filter((v) => v.id === -1);

  model.value.codes = [
    ...model.value.codes.filter((v) => v.id !== -1),
    ...codes.map((l) => {
      return {
        ...l,
        validityDate: validity.value
          ? addHours(
              parse(validity.value, 'dd-MM-yyyy', new Date()),
              12,
            ).toISOString()
          : null,
      };
    }),
  ];
}

const showCodeInfo = ref<boolean>(false);
function showInfo() {
  showCodeInfo.value = !showCodeInfo.value;
}
</script>
<template>
  <v-form v-model="valid" @submit.prevent="saveItem">
    <v-container>
      <h2>{{ $t(action) }} Discount <CurrentLanguage /></h2>
      <v-alert type="info"> A discount can be added to an initiative. </v-alert>
      <v-row class="mt-8">
        <v-col>
          <v-text-field
            v-model="model.title"
            label="Title"
            hide-details
            required
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-text-field
            v-model="model.description"
            label="Description"
            hide-details
            required
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-text-field
            v-model="model.emailText"
            label="Email text"
            hide-details
            required
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-autocomplete
            v-model="model.initiative"
            :items="initiatives"
            label="Initiative"
            item-text="title"
            return-object
            required
          ></v-autocomplete>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="6">
          <v-file-input
            v-model="model.codesFile"
            label="Codes CSV"
            @change="parseCSV"
            :hint="codes ? `${codes} codes found` : ''"
            append-icon="mdi-help-circle-outline"
            @click:append="showInfo"
          ></v-file-input>
        </v-col>
        <v-col>
          <DateField
            v-model="validity"
            label="Validity Date"
            @change="updateCodes"
          ></DateField>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="2"></v-col>
        <v-col cols="6">
          <v-alert type="warning" v-if="showCodeInfo">
            Upload a CSV with a single column that contains the code. Ensure the
            column has a header row. It should looks something like this:

            <pre style="margin: 16px 24px 0 24px">
                Code
                abc1
                abc2
                abc3
                ...
              </pre
            >
          </v-alert>
        </v-col>
        <v-col cols="2"></v-col>
      </v-row>
      <v-card
        elevation="2"
        variant="tonal"
        class="ml-10 mr-10"
        v-if="codeGroups.length"
      >
        <v-card-text>
          <h3>Codes</h3>
          <ul class="ml-8">
            <li v-for="group in codeGroups" :key="`${group.validity}`">
              {{ group.usedCodes }} used /{{ group.codes }} total<i
                v-if="group.validity"
                >, valid until
                {{ format(new Date(group.validity), 'do MMMM yyyy') }}</i
              >
            </li>
          </ul>
        </v-card-text>
      </v-card>
    </v-container>
    <v-row>
      <v-col col="2"></v-col>
      <v-col>
        <v-btn
          class="mt-2"
          type="button"
          color="warning"
          @click="removeItem"
          block
          v-if="model.id !== -1"
          >Delete</v-btn
        >
      </v-col>
      <v-col>
        <v-btn class="mt-2" type="button" @click="cancel" block>Cancel</v-btn>
      </v-col>
      <v-col>
        <v-btn class="mt-2" type="submit" color="success" block>Edit</v-btn>
      </v-col>
      <v-col col="2"></v-col>
    </v-row>
  </v-form>
</template>
